import i18next, {} from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, LOCALES } from '@instanexus/contracts';
import enAuth from '../locales/en/auth.json' with { type: 'json' };
import enCommon from '../locales/en/common.json' with { type: 'json' };
import enDirectory from '../locales/en/directory.json' with { type: 'json' };
import enErrors from '../locales/en/errors.json' with { type: 'json' };
import enMailroom from '../locales/en/mailroom.json' with { type: 'json' };
import enPlatform from '../locales/en/platform.json' with { type: 'json' };
import enSurvey from '../locales/en/survey.json' with { type: 'json' };
import enVisitor from '../locales/en/visitor.json' with { type: 'json' };
import esAuth from '../locales/es/auth.json' with { type: 'json' };
import esCommon from '../locales/es/common.json' with { type: 'json' };
import esDirectory from '../locales/es/directory.json' with { type: 'json' };
import esErrors from '../locales/es/errors.json' with { type: 'json' };
import esMailroom from '../locales/es/mailroom.json' with { type: 'json' };
import esPlatform from '../locales/es/platform.json' with { type: 'json' };
import esSurvey from '../locales/es/survey.json' with { type: 'json' };
import esVisitor from '../locales/es/visitor.json' with { type: 'json' };
import frAuth from '../locales/fr/auth.json' with { type: 'json' };
import frCommon from '../locales/fr/common.json' with { type: 'json' };
import frDirectory from '../locales/fr/directory.json' with { type: 'json' };
import frErrors from '../locales/fr/errors.json' with { type: 'json' };
import frMailroom from '../locales/fr/mailroom.json' with { type: 'json' };
import frPlatform from '../locales/fr/platform.json' with { type: 'json' };
import frSurvey from '../locales/fr/survey.json' with { type: 'json' };
import frVisitor from '../locales/fr/visitor.json' with { type: 'json' };
/**
 * The eight namespaces. Fixed set — a namespace maps to one app or one platform
 * area (docs/011-i18n.md). Adding one means adding a file in every locale.
 */
export const NAMESPACES = [
    'common',
    'auth',
    'errors',
    'platform',
    'visitor',
    'directory',
    'survey',
    'mailroom',
];
/** `t('actions.save')` with no namespace prefix resolves here. */
export const DEFAULT_NAMESPACE = 'common';
/**
 * All bundles are imported statically so the built package carries its own
 * translations — no runtime backend, no network fetch, nothing to configure.
 */
export const resources = {
    en: {
        common: enCommon,
        auth: enAuth,
        errors: enErrors,
        platform: enPlatform,
        visitor: enVisitor,
        directory: enDirectory,
        survey: enSurvey,
        mailroom: enMailroom,
    },
    es: {
        common: esCommon,
        auth: esAuth,
        errors: esErrors,
        platform: esPlatform,
        visitor: esVisitor,
        directory: esDirectory,
        survey: esSurvey,
        mailroom: esMailroom,
    },
    fr: {
        common: frCommon,
        auth: frAuth,
        errors: frErrors,
        platform: frPlatform,
        visitor: frVisitor,
        directory: frDirectory,
        survey: frSurvey,
        mailroom: frMailroom,
    },
};
const EN_RESOURCES = resources.en;
/**
 * The `en` string for a (possibly namespaced) key, or an empty string.
 * A user must never see a raw key (docs/011-i18n.md, "Missing keys").
 */
export function englishFallback(key) {
    const separatorIndex = key.indexOf(':');
    const namespace = separatorIndex === -1 ? DEFAULT_NAMESPACE : key.slice(0, separatorIndex);
    const path = separatorIndex === -1 ? key : key.slice(separatorIndex + 1);
    let node = EN_RESOURCES[namespace];
    for (const segment of path.split('.')) {
        if (typeof node !== 'object' || node === null)
            return '';
        node = node[segment];
    }
    return typeof node === 'string' ? node : '';
}
/**
 * Builds an isolated i18next instance. A factory rather than a module-level
 * singleton because the server renders emails in the *recipient's* locale and
 * therefore needs more than one instance alive at a time.
 */
export function createI18n(options = {}) {
    const { locale = DEFAULT_LOCALE, logMissingKey, bindReact = true } = options;
    const instance = i18next.createInstance();
    const reported = new Set();
    if (bindReact)
        instance.use(initReactI18next);
    void instance.init({
        resources,
        lng: locale,
        fallbackLng: DEFAULT_LOCALE,
        supportedLngs: [...LOCALES],
        defaultNS: DEFAULT_NAMESPACE,
        ns: [...NAMESPACES],
        interpolation: { escapeValue: false },
        saveMissing: logMissingKey !== undefined,
        missingKeyHandler: (locales, namespace, key) => {
            if (logMissingKey === undefined)
                return;
            const identity = `${namespace}:${key}`;
            if (reported.has(identity))
                return;
            reported.add(identity);
            logMissingKey({ locales, namespace, key });
        },
        parseMissingKeyHandler: (key, defaultValue) => englishFallback(key) || defaultValue || '',
    });
    return instance;
}
//# sourceMappingURL=config.js.map